import { useState, useMemo } from 'react';
import { COLORS } from '../../constants/theme';
import { BUILDING_PAIRS, BUILDING_MAP } from '../../constants/buildings';
import { ConfirmModal } from '../ConfirmModal';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { LandDistributionBar } from './LandDistributionBar';
import { NumericInput } from '../strategy/NumericInput';
import { formatNumber } from '../../utils/format';
import { computeAutoRaze } from '../../utils/buildAutoPopulate';
import { computeRazeCostPerAcre } from './razeCost';
import { api } from '../../api/client';
import type { Province } from '../../types/province';
import type { RazeRequest, RazeResponse } from '../../types/raze';
import type { ActiveStrategyDto } from '../../types/strategy';

interface RazePanelProps {
  province: Province;
  activeStrategy: ActiveStrategyDto | null;
  onRazeSuccess: () => void;
  worldId: string;
  provinceId: string;
}

export function RazePanel({ province, activeStrategy, onRazeSuccess, worldId, provinceId }: RazePanelProps) {
  const [razeQty, setRazeQty] = useState<Record<string, number>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const razeCostPerAcre = useMemo(() => computeRazeCostPerAcre(province), [province]);

  const totalToRaze = useMemo(
    () => Object.values(razeQty).reduce((sum, q) => sum + q, 0),
    [razeQty]
  );

  const totalRazeCost = totalToRaze * razeCostPerAcre;
  const availableGold = province.resources.gold;
  const totalLand = province.territory.totals.acres;
  const barren = province.territory.totals.barren;
  const totalOwned = useMemo(
    () =>
      Object.values(province.territory.buildings).reduce((sum, count) => sum + (count || 0), 0),
    [province]
  );

  const visiblePairs = BUILDING_PAIRS;

  const handleRazeOptimize = () => {
    if (!activeStrategy?.settings) return;
    const quantities = computeAutoRaze(
      activeStrategy.settings.buildings,
      province.territory.totals.acres,
      province.territory.buildings as Record<string, number>
    );
    setRazeQty(quantities);
  };


  const handleClear = () => {
    setRazeQty({});
    setSubmitError(null);
    setSuccessMessage(null);
  };

  const handleSubmitClick = () => {
    setSubmitError(null);
    setSuccessMessage(null);

    if (totalToRaze === 0) {
      setSubmitError('Please enter at least one building to raze');
      return;
    }

    if (totalRazeCost > availableGold) {
      setSubmitError(
        `Insufficient gold: need ${formatNumber(totalRazeCost)} gc, have ${formatNumber(availableGold)} gc`
      );
      return;
    }

    if (totalToRaze > 10) {
      setShowConfirmModal(true);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const buildings: Record<string, number> = {};
      Object.entries(razeQty).forEach(([id, qty]) => {
        if (qty > 0) buildings[id] = qty;
      });

      const request: RazeRequest = { buildings };
      const response = await api.post<RazeResponse>(
        `/api/worlds/${worldId}/provinces/${provinceId}/raze`,
        request
      );

      if (response.error) {
        setSubmitError(response.error);
      } else if (response.data) {
        const result = response.data.result;
        setSuccessMessage(
          `Razed ${result.totalAcresRazed} acres for ${formatNumber(result.totalCost)} gc. ${formatNumber(result.remainingGold)} gc remaining.`
        );
        setRazeQty({});
        onRazeSuccess();
      }
    } catch {
      setSubmitError('Failed to submit raze request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const razeBuildingList = Object.entries(razeQty)
    .filter(([, qty]) => qty > 0)
    .map(([id, qty]) => {
      const building = BUILDING_MAP[id];
      return building ? `${qty} ${building.name}` : '';
    })
    .filter(Boolean);

  return (
    <>
      <div className="space-y-4">
        {/* Stats Row */}
        <div className="flex gap-3">
          <div className="w-36">
            <StatCard
              label="Razeable Acres"
              value={`${formatNumber(totalOwned)} acres`}
              sub={`${formatNumber(barren)} barren`}
              borderless
            />
          </div>
          <div className="w-36">
            <StatCard
              label="Built Land"
              value={`${formatNumber(totalOwned)} acres`}
              sub={`of ${formatNumber(totalLand)} total`}
              borderless
            />
          </div>
          <div className="w-36">
            <StatCard
              label="Raze Cost"
              value={`${formatNumber(razeCostPerAcre)} gc/acre`}
              sub="Per acre demolished"
              borderless
            />
          </div>
        </div>

        {/* Land Distribution Bar */}
        <div className="px-4">
          <LandDistributionBar
            totalLand={totalLand}
            builtAcres={totalOwned}
            pendingAcres={0}
            barrenAcres={barren + totalToRaze}
          />
        </div>

        {/* Raze table (grouped) */}
        <div
          className="rounded overflow-hidden"
          style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
        >
          {/* Column Headers */}
          <div className="grid grid-cols-2" style={{ backgroundColor: COLORS.sectionHeader }}>
            {[0, 1].map((col) => (
              <div
                key={col}
                className="grid items-center px-3 py-1.5"
                style={{
                  gridTemplateColumns: '1fr 64px 64px 88px',
                  borderLeft: col === 1 ? `1px solid ${COLORS.border}` : undefined,
                }}
              >
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Building</span>
                <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>HAVE</span>
                <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>AFTER</span>
                <span className="text-center text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>RAZE</span>
              </div>
            ))}
          </div>
          {/* Building Pairs */}
          {visiblePairs.length === 0 ? (
            <p className="px-4 py-4 text-sm" style={{ color: COLORS.text.tertiary }}>
              No buildings to raze.
            </p>
          ) : (
            visiblePairs.map(([leftId, rightId]) => {
              const renderCell = (id: string) => {
                const building = BUILDING_MAP[id];
                if (!building) return null;
                const owned = province.territory.buildings[building.id] || 0;
                const targetPct = activeStrategy?.settings?.buildings[building.id] ?? null;
                const targetCount = targetPct !== null ? Math.floor((totalLand * targetPct) / 100) : null;
                const qty = razeQty[building.id] || 0;
                const afterOwned = Math.max(0, owned - qty);
                const afterBelowTarget = targetCount !== null && qty > 0 && afterOwned < targetCount;

                return (
                  <div
                    className="building-row-highlight grid items-center px-3 py-1.5"
                    style={{ gridTemplateColumns: '1fr 64px 64px 88px' }}
                  >
                    <span className="text-sm" style={{ color: owned > 0 ? COLORS.text.primary : COLORS.text.tertiary }}>{building.name}</span>
                    <span className="text-center font-mono text-sm" style={{ color: owned > 0 ? COLORS.text.secondary : COLORS.text.tertiary }}>{formatNumber(owned)}</span>
                    <span
                      className="text-center font-mono text-sm"
                      style={{
                        color: qty > 0
                          ? afterBelowTarget ? COLORS.negative : COLORS.text.secondary
                          : COLORS.text.tertiary,
                      }}
                    >
                      {qty > 0 ? formatNumber(afterOwned) : ''}
                    </span>
                    <div className="flex items-center justify-end gap-1">
                      <NumericInput
                        value={qty}
                        max={owned}
                        onChange={(v) => {
                          const maxOwned = province.territory.buildings[building.id] || 0;
                          setRazeQty((prev) => ({ ...prev, [building.id]: Math.min(v, maxOwned) }));
                        }}
                        className="w-14"
                        ariaLabel={`Raze quantity for ${building.name}`}
                      />
                      <div style={{ width: '26px', height: '26px', flexShrink: 0 }}>
                        {targetCount !== null && owned > targetCount && (
                          <button
                            onClick={() => {
                              const maxOwned = province.territory.buildings[building.id] || 0;
                              setRazeQty((prev) => ({ ...prev, [building.id]: Math.min(owned - targetCount!, maxOwned) }));
                            }}
                            aria-label={`Fill raze for ${building.name} to strategy target`}
                            style={{
                              width: '26px',
                              height: '26px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '4px',
                              border: `1px solid ${COLORS.border}`,
                              backgroundColor: COLORS.sectionHeader,
                              color: '#B89878',
                              cursor: 'pointer',
                            }}
                          >
                            ⚡
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              };

              return (
                <div key={`${leftId}-${rightId}`} className="grid grid-cols-2" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <div>{renderCell(leftId)}</div>
                  <div style={{ borderLeft: `1px solid ${COLORS.border}` }}>{renderCell(rightId)}</div>
                </div>
              );
            })
          )}


          {/* Submit Bar */}
          <div
            className="px-4 py-3 space-y-3"
            style={{ borderTop: `1px solid ${COLORS.border}` }}
          >
          {/* Messages */}
          {submitError && (
            <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
          )}
          {successMessage && (
            <div className="text-sm" style={{ color: COLORS.positive }}>{successMessage}</div>
          )}

          {/* Summary row */}
          <div className="flex items-center gap-6 text-sm">
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Razing: </span>
              <span className="font-mono" style={{ color: COLORS.text.primary }}>
                {formatNumber(totalToRaze)} acres
              </span>
            </span>
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Raze Cost: </span>
              <span className="font-mono" style={{ color: totalRazeCost > 0 ? COLORS.gold : COLORS.text.tertiary }}>
                {formatNumber(totalRazeCost)} gc
              </span>
            </span>
            {totalRazeCost > availableGold && totalToRaze > 0 && (
              <span className="text-xs" style={{ color: COLORS.negative }}>
                Insufficient gold
              </span>
            )}
          </div>

          {/* Actions row */}
          <div className="flex items-center gap-4">
            {activeStrategy?.settings && (
              <button
                onClick={handleRazeOptimize}
                className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded"
                style={{
                  backgroundColor: COLORS.sectionHeader,
                  border: `1px solid ${COLORS.border}`,
                  color: '#B89878',
                  cursor: 'pointer',
                }}
              >
                ⚡ Raze to Strategy
              </button>
            )}

            <div className="flex gap-2 ml-auto">
              <Button variant="secondary" onClick={handleClear} disabled={totalToRaze === 0}>
                Clear
              </Button>
              <Button
                onClick={handleSubmitClick}
                isLoading={isSubmitting}
                disabled={totalToRaze === 0}
                style={{ backgroundColor: COLORS.negative, borderColor: COLORS.negative }}
              >
                Raze Buildings
              </Button>
            </div>
          </div>
        </div>
        </div>

      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleSubmit}
        onCancel={() => setShowConfirmModal(false)}
        title="Confirm Raze"
        confirmText="Confirm Raze"
      >
        <div>
          <p className="mb-3">
            Are you sure you want to raze {totalToRaze} acres for {formatNumber(totalRazeCost)} gc?
          </p>
          <ul className="list-disc list-inside mb-3 space-y-1 text-sm">
            {razeBuildingList.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      </ConfirmModal>
    </>
  );
}
